import { useTranslation } from "react-i18next";
import { Checkbox } from "@core/components";
import type { Consent } from "../../types";

/**
 * « Vos accords » (US-E3-10) — one checkbox per legal agreement, required ones first.
 *
 * The checkbox's own label is the consent's `content` from the API — the FULL plain-language
 * statement, not a shorter hand-written mirror. Jira's own rule ("each is stated in plain language
 * before the box beside it") is exactly what that field already carries; inventing a shorter
 * summary here would be a second, competing wording of the same legal text. Today that content is
 * synthetic placeholder copy (real legal text is a pending client input, same gap as
 * `config/acquire/legal-documents.approved.json`) — rendering it as-is, unedited, is what makes a
 * real-copy swap a content change only, never a frontend one.
 *
 * Each toggle commits immediately (`PUT .../consents/{type}/`), same as every other
 * complete-in-one-click control in this dossier — there is no separate "confirm" step for a single
 * checkbox.
 */
export function ConsentPanel({
  consents,
  onToggle,
  pendingConsentType,
  isDisabled,
}: {
  consents: Consent[];
  onToggle: (consentType: string, accepted: boolean) => void;
  pendingConsentType: string | null;
  isDisabled: boolean;
}) {
  const { t } = useTranslation("acquire");

  if (consents.length === 0) return null;

  const required = consents.filter((consent) => consent.is_required);
  const optional = consents.filter((consent) => !consent.is_required);

  return (
    <section className="flex flex-col gap-md border-t border-b border-(--border-default) bg-surface-card px-md py-lg -mx-md sm:-mx-xl sm:px-xl xl:py-xl">
      <h2 className="text-md-semibold text-(--text-primary)">
        {t("dossier.review.consent-title")}
      </h2>

      {required.length > 0 ? (
        <div className="flex flex-col gap-md">
          <p className="text-xs-semibold text-(--text-tertiary)">
            {t("dossier.review.consent-required-label")}
          </p>
          {required.map((consent) => (
            <Checkbox
              key={consent.consent_type}
              isSelected={consent.accepted}
              isDisabled={
                isDisabled || pendingConsentType === consent.consent_type
              }
              onChange={(checked) => onToggle(consent.consent_type, checked)}
            >
              {consent.content}
            </Checkbox>
          ))}
        </div>
      ) : null}

      {optional.length > 0 ? (
        <div className="flex flex-col gap-md">
          <p className="text-xs-semibold text-(--text-tertiary)">
            {t("dossier.review.consent-optional-label")}
          </p>
          {optional.map((consent) => (
            <Checkbox
              key={consent.consent_type}
              isSelected={consent.accepted}
              isDisabled={
                isDisabled || pendingConsentType === consent.consent_type
              }
              onChange={(checked) => onToggle(consent.consent_type, checked)}
            >
              {consent.content}
            </Checkbox>
          ))}
        </div>
      ) : null}
    </section>
  );
}
